import * as React from 'react'
import { AlertTriangle, Plus } from 'lucide-react'
import { useVenueMarketing, useSaveVenueMarketing } from '@/hooks/useVenueMarketing'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { FormActions } from '@/components/ui/form-actions'
import { FormError } from '@/components/ui/form-error'
import { ErrorState } from '@/components/ui/states'
import { SectionList } from '@/components/marketing/SectionList'
import { blankSection, MARKETING_SECTIONS } from '@/components/marketing/sections'
import {
  noProblems,
  pathKey,
  problemsFrom,
  setAt,
  type Draft,
  type DraftProblems,
} from '@/components/marketing/draft'
import { saveVenueMarketingContentSchema } from '@lib/input/venue-marketing.input'
import { isJsonObject } from '@lib/helpers/json'
import type { VenueDto } from '@lib/dto/venue.dto'
import { marketingPagesToOffer } from '@lib/helpers/marketing-pages'

/**
 * A venue's marketing content — the documents the public pricing sites are built
 * from.
 *
 * One page per track for a venue that runs several, plus the venue's own. The
 * form is the whole contract: twenty-six sections, fifteen of them always
 * present and eleven added when a venue has something to put in them, because
 * presence is the on switch and there is no enabled flag anywhere.
 *
 * **It refuses to save content the contract would reject**, and says where. The
 * same schema the API validates against runs here first, and because Zod reports
 * an issue at a path, each complaint lands under the field that caused it
 * without a table mapping one to the other.
 *
 * See `docs/venue-marketing.md`.
 */
export function VenueMarketing({
  venue,
  canEdit,
}: {
  venue: VenueDto
  /** Edit adds changing, adding, removing and reordering sections, and saving; without it the page is only read. */
  canEdit: boolean
}): React.ReactElement {
  const pages = useVenueMarketing(venue.id)
  const save = useSaveVenueMarketing()

  const choices = marketingPagesToOffer(venue, pages.data ?? [])
  const [chosen, setChosen] = React.useState<string | null>(null)
  const page = pages.data?.find((candidate) => candidate.eventTrackId === chosen)

  const [draft, setDraft] = React.useState<Draft | null>(null)
  const [problems, setProblems] = React.useState<DraftProblems>(noProblems())
  const [open, setOpen] = React.useState<ReadonlySet<string>>(new Set())
  const [saved, setSaved] = React.useState(false)

  // The chosen page's stored document becomes the draft. Keyed on the page's id
  // and the choice so switching tracks reloads rather than carrying edits across.
  const loadedKey = `${page?.id ?? 'none'}:${chosen ?? 'venue'}`
  const [loadedFrom, setLoadedFrom] = React.useState<string | null>(null)
  // Falls Creek has two tracks and no venue-wide page, so starting on the venue
  // page would open an empty form for a page that should not exist. Picked per
  // venue: the section stays mounted when the next venue is already loaded, and
  // the last venue's track is not one of this venue's pages.
  const [pickedFor, setPickedFor] = React.useState<string | null>(null)
  if (pages.isSuccess && pickedFor !== venue.id) {
    setPickedFor(venue.id)
    const first = choices[0]
    setChosen(first === undefined ? null : first.eventTrackId)
  }
  if (pages.isSuccess && loadedFrom !== loadedKey) {
    setLoadedFrom(loadedKey)
    setDraft(isJsonObject(page?.content) ? page.content : {})
    setProblems(noProblems())
    setSaved(false)
  }

  function change(next: Draft): void {
    setDraft(next)
    setSaved(false)
  }

  function submit(): void {
    if (draft === null) return
    const parsed = saveVenueMarketingContentSchema.safeParse(draft)
    if (!parsed.success) {
      const found = problemsFrom(parsed.error.issues)
      setProblems(found)
      // Open every section with something wrong inside it, so nothing is
      // reported into a collapsed panel nobody thinks to expand.
      setOpen(
        new Set(
          MARKETING_SECTIONS.filter(
            (spec) => (found.bySubtree.get(pathKey([spec.key])) ?? []).length > 0,
          ).map((spec) => spec.key),
        ),
      )
      return
    }
    setProblems(noProblems())
    save.mutate(
      { venueId: venue.id, eventTrackId: chosen, content: parsed.data },
      {
        onSuccess: () => {
          setSaved(true)
        },
      },
    )
  }

  function toggle(key: string): void {
    const next = new Set(open)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    setOpen(next)
  }

  if (pages.isLoading) return <p className="text-sm text-ink-muted">Loading…</p>
  if (pages.error !== null) {
    return (
      <ErrorState
        message={requestErrorMessage(pages.error)}
        onRetry={() => {
          void pages.refetch()
        }}
      />
    )
  }
  if (draft === null) return <p className="text-sm text-ink-muted">Loading…</p>

  const absent = MARKETING_SECTIONS.filter(
    (spec) => spec.optional && draft[spec.key] === undefined,
  )
  const failedToSave = save.error !== null
  // Reported against the document rather than any field: Zod gives these an
  // empty path, which is `''` once joined.
  const documentProblems = problems.byPath.get('') ?? []

  return (
    <div className="space-y-5">
      {choices.length > 1 ? (
        <div className="flex flex-wrap items-center gap-1">
          {choices.map((choice) => (
            <Button
              key={choice.eventTrackId ?? 'venue'}
              type="button"
              variant={choice.eventTrackId === chosen ? 'default' : 'ghost'}
              size="sm"
              onClick={() => {
                setChosen(choice.eventTrackId)
              }}
            >
              {choice.label}
            </Button>
          ))}
        </div>
      ) : null}

      {page === undefined ? (
        <p className="text-sm text-ink-muted">
          {canEdit
            ? 'This page has nothing on it yet. Fill the sections in and save to create it.'
            : 'This page has nothing on it yet.'}
        </p>
      ) : null}

      {page?.problems != null && page.problems.length > 0 ? (
        <div className="flex gap-2 rounded-md border border-line bg-surface-sunken/40 p-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-danger-ink" aria-hidden />
          <div className="min-w-0 space-y-1">
            <p className="text-sm font-medium">
              This page is not being served — it no longer satisfies the contract.
            </p>
            <ul className="space-y-0.5">
              {page.problems.map((problem) => (
                <li key={problem} className="text-xs text-ink-muted">
                  {problem}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <div className="space-y-2">
        <p className="text-xs text-ink-muted">
          {canEdit
            ? 'The order these appear in is the order the public page renders them. Drag a section, or use its arrows; the top of the page and the footer are fixed. Open one to edit it.'
            : 'The order these appear in is the order the public page renders them. Open one to see what it says.'}
        </p>
        <SectionList
          draft={draft}
          onChange={change}
          problems={problems}
          venueId={venue.id}
          open={open}
          onToggle={toggle}
          canEdit={canEdit}
        />
      </div>

      {canEdit && absent.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs text-ink-muted">
            Sections this page does not have. Adding one is what turns it on.
          </p>
          <div className="flex flex-wrap gap-1">
            {absent.map((spec) => (
              <Button
                key={spec.key}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  change(setAt(draft, [spec.key], blankSection(spec)))
                  setOpen(new Set([...open, spec.key]))
                }}
              >
                <Plus className="mr-1 size-3.5" aria-hidden />
                {spec.label}
              </Button>
            ))}
          </div>
        </div>
      ) : null}

      {/* A rule about the whole document — lodging and overnight stays together —
          is reported at the root, where no section owns it. Without this the save
          was refused and the reason appeared nowhere at all. */}
      {documentProblems.length > 0 ? (
        <ul className="space-y-1">
          {documentProblems.map((problem) => (
            <li key={problem} className="text-sm text-danger-ink">
              {problem}
            </li>
          ))}
        </ul>
      ) : null}
      {problems.byPath.size > documentProblems.length ? (
        <FormError>
          Some sections need fixing before this can be saved. They are marked above.
        </FormError>
      ) : null}
      {failedToSave ? (
        <FormError>{requestErrorMessage(save.error)}</FormError>
      ) : null}

      {canEdit ? (
        <FormActions align="start">
          <Button type="button" onClick={submit} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          {saved ? <span className="text-sm text-ink-muted">Saved.</span> : null}
        </FormActions>
      ) : null}
    </div>
  )
}
